import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledSkillsSection = styled.section`
  max-width: 900px;

  .skills-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    grid-gap: 15px;
    margin-top: 20px;
  }

  article {
    ${({ theme }) => theme.mixins.boxShadow};
    padding: 1.5rem 1.4rem;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);

    h3 {
      color: var(--lightest-slate);
      font-size: var(--fz-lg);
      margin: 0 0 12px;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style: none;
    }

    li {
      position: relative;
      padding-left: 18px;
      margin-bottom: 8px;
      color: var(--slate);
      font-size: var(--fz-sm);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
      }
    }
  }
`;

const groups = [
  {
    title: 'LLM foundations',
    items: [
      'Transformers, tokenization, embeddings, and attention',
      'Context windows, temperature, and top-p',
      'What happens when a message hits an LLM',
    ],
  },
  {
    title: 'API platforms',
    items: [
      'OpenAI, Claude, and Gemini APIs',
      'Streaming responses and local models with Ollama',
      'Choosing models for cost, speed, and quality',
    ],
  },
  {
    title: 'Prompt engineering',
    items: [
      'Zero-shot, few-shot, role, CoT, ReAct, and self-consistency',
      'Structured JSON output and prompt chaining',
      'Constraints, negative prompts, and reliability',
    ],
  },
  {
    title: 'RAG & retrieval',
    items: [
      'Indexing vs query pipelines and chunking strategies',
      'Qdrant vector search, metadata filters, and reranking',
      'Queue-based ingestion and vectorless / hybrid retrieval',
    ],
  },
  {
    title: 'Agentic systems',
    items: [
      'Perceive-decide-act loops, tools, and guardrails',
      'OpenAI Agents SDK, Claude Agent SDK, and MCP',
      'Memory layers with Mem0 and knowledge graphs',
    ],
  },
  {
    title: 'AI products shipped',
    items: [
      'Streaming ChatGPT clone with history and token limits',
      'NotebookLM-style multi-document reasoning',
      'CLI agents, voice agents, AutoWiki for Git, and AI pitch decks',
    ],
  },
];

const Skills = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledSkillsSection id="skills" ref={revealContainer}>
      <h2 className="numbered-heading">What I’ve been learning</h2>
      <p>
        I’ve been upskilling across the modern AI stack — from transformer internals to production
        agents — and applying it in projects, quizzes, hackathons, and peer-reviewed work.
      </p>
      <div className="skills-grid">
        {groups.map(group => (
          <article key={group.title}>
            <h3>{group.title}</h3>
            <ul>
              {group.items.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </StyledSkillsSection>
  );
};

export default Skills;
